import { useEffect, useRef, useCallback } from 'react';

/**
 * useUnsavedChanges — warns users before leaving a page with unsaved changes.
 *
 * Usage:
 *   const { markDirty, markClean, isDirty } = useUnsavedChanges(hasChanges);
 *
 * Parameters:
 *   hasChanges – boolean, whether there are unsaved changes.
 *   message    – optional custom warning message (browser native dialogs ignore this on some browsers).
 *
 * The hook:
 *  1. Blocks tab/window close with the browser beforeunload dialog.
 *  2. Can be checked before navigation: isDirty + window.confirm in onClick handlers.
 */
export function useUnsavedChanges(hasChanges = false, message = 'You have unsaved changes. Are you sure you want to leave?') {
  const isDirty = !!hasChanges;

  // Block browser close / refresh
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, message]);

  /**
   * confirmLeave — call before a programmatic navigation.
   * Returns true if safe to proceed (either no changes, or user confirmed).
   */
  const confirmLeave = useCallback(
    (customMessage) => {
      if (!isDirty) return true;
      return window.confirm(customMessage || message);
    },
    [isDirty, message]
  );

  return { isDirty, confirmLeave };
}

export default useUnsavedChanges;
